type AudioBufferLike = Pick<AudioBuffer, "length" | "numberOfChannels" | "sampleRate" | "getChannelData">;

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

export function encodeAudioBufferAsWav(buffer: AudioBufferLike): Blob {
  if (!buffer.length || !buffer.numberOfChannels || !buffer.sampleRate) {
    throw new Error("录音内容为空，请重试");
  }
  const output = new ArrayBuffer(44 + buffer.length * 2);
  const view = new DataView(output);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + buffer.length * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, buffer.length * 2, true);

  const channels = Array.from(
    { length: buffer.numberOfChannels },
    (_, index) => buffer.getChannelData(index),
  );
  for (let frame = 0; frame < buffer.length; frame += 1) {
    const mixed = channels.reduce((sum, channel) => sum + (channel[frame] ?? 0), 0) / channels.length;
    const sample = Math.max(-1, Math.min(1, mixed));
    view.setInt16(44 + frame * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return new Blob([output], { type: "audio/wav" });
}

type AudioContextConstructor = new () => AudioContext;

export async function recordingToWav(blob: Blob): Promise<Blob> {
  if (!blob.size) throw new Error("没有录到声音，请重试");
  if (blob.type.toLowerCase().includes("wav")) return blob;
  const browserWindow = window as Window & { webkitAudioContext?: AudioContextConstructor };
  const Context = window.AudioContext ?? browserWindow.webkitAudioContext;
  if (!Context) throw new Error("当前浏览器无法转换录音格式，请改用文字指令");
  const context = new Context();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    return encodeAudioBufferAsWav(decoded);
  } catch (error) {
    if (error instanceof Error && error.message.includes("录音")) throw error;
    throw new Error("录音格式转换失败，请重试");
  } finally {
    await context.close().catch(() => undefined);
  }
}
